import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
const API_URL = process.env.REACT_APP_BACKEND_URL;
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Home, Mic, MicOff, Play, Pause, Trash2, 
  Video, VideoOff, Volume2, RefreshCw, Save, Clock
} from 'lucide-react';

const practiceQuestions = [
  { id: 1, q: 'Kendinizi tanıtır mısınız?', time: 60, difficulty: 'Kolay' },
  { id: 2, q: 'Neden kabin memuru olmak istiyorsunuz?', time: 60, difficulty: 'Kolay' },
  { id: 3, q: 'Zor bir müşteriyle nasıl başa çıkarsınız?', time: 90, difficulty: 'Orta' },
  { id: 4, q: 'Takım çalışmasını nasıl tanımlarsınız?', time: 60, difficulty: 'Kolay' },
  { id: 5, q: 'Stresli durumlarla nasıl başa çıkarsınız?', time: 90, difficulty: 'Orta' },
  { id: 6, q: 'Neden THY\'de çalışmak istiyorsunuz?', time: 90, difficulty: 'Orta' },
  { id: 7, q: 'Bir yolcu alkollü ve agresif. Ne yaparsınız?', time: 120, difficulty: 'Zor' },
  { id: 8, q: 'Are you willing to relocate to Dubai?', time: 60, difficulty: 'Orta' },
  { id: 9, q: 'Tell me about a time you worked in a team', time: 90, difficulty: 'Zor' },
  { id: 10, q: '5 yıl sonra kendinizi nerede görüyorsunuz?', time: 60, difficulty: 'Orta' }
];

export default function InterviewSelfRecord({ onBack }) {
  const [mode, setMode] = useState('select'); // select, record, playback
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordings, setRecordings] = useState([]);
  const [savedRecordings, setSavedRecordings] = useState([]);
  const [showSavedRecordings, setShowSavedRecordings] = useState(true);
  const [currentRecording, setCurrentRecording] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [useVideo, setUseVideo] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    return () => {
      // Cleanup
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);


  // Load saved recordings from server
  useEffect(() => {
    const deleteRecording = async (recordingId) => {
    if (!window.confirm("Bu kaydı silmek istediğinizden emin misiniz?")) return;
    
    try {
      await axios.delete(`${API_URL}/api/voice-recordings/${recordingId}`, { withCredentials: true });
      toast.success("Kayıt silindi");
      loadSavedRecordings();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Kayıt silinemedi");
    }
  };

  const loadSavedRecordings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/voice-recordings/my-recordings`, { withCredentials: true });
        if (response.data && response.data.recordings) {
          // Show all recordings, interview ones at top
          const allRecs = response.data.recordings || [];
          const interviewRecs = allRecs.filter(r => r.category === "interview" || r.question_text?.includes("mısınız") || r.question_text?.includes("?"));
          const otherRecs = allRecs.filter(r => !interviewRecs.includes(r));
          setSavedRecordings([...interviewRecs, ...otherRecs]);
        }
      } catch (error) {
        console.error("Error loading saved recordings:", error);
      }
    };
    loadSavedRecordings();
  }, []);

  const startRecording = async () => {
    try {
      const constraints = useVideo 
        ? { audio: true, video: { facingMode: 'user' } }
        : { audio: true };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      if (useVideo && videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const mimeType = useVideo ? 'video/webm' : 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);
        
        const newRecording = {
          id: Date.now(),
          questionId: selectedQuestion.id,
          question: selectedQuestion.q,
          url,
          duration: recordingTime,
          type: useVideo ? 'video' : 'audio',
          timestamp: new Date().toLocaleString('tr-TR')
        };
        
        setRecordings([newRecording, ...recordings]);
        setCurrentRecording(newRecording);

        // Save to server
        const reader = new FileReader();
        reader.onloadend = async () => {
          try {
            await axios.post(`${API_URL}/api/voice-recordings/save`, {
              exercise_id: "interview_" + selectedQuestion.id,
              exercise_title: selectedQuestion.q,
              category: "interview",
              audio_data: reader.result,
              duration_seconds: recordingTime
            }, { withCredentials: true });
          } catch (error) {
            console.error("Error saving recording:", error);
          }
        };
        reader.readAsDataURL(blob);

        setMode('playback');
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= selectedQuestion.time) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
      
    } catch (err) {
      console.error('Recording error:', err);
      alert('Mikrofon/kamera erişimi reddedildi. Lütfen tarayıcı izinlerini kontrol edin.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const deleteRecording = (id) => {
    setRecordings(recordings.filter(r => r.id !== id));
    if (currentRecording?.id === id) {
      setCurrentRecording(null);
      setMode('select');
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Kolay': return 'bg-green-100 text-green-700';
      case 'Orta': return 'bg-yellow-100 text-yellow-700';
      case 'Zor': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Question selection screen
  if (mode === 'select') {
    return (
      <div className="min-h-screen bg-gray-50 pb-8">
        <div className="bg-gradient-to-br from-purple-600 to-pink-600 px-4 pt-6 pb-8 rounded-b-[2rem]">
          <div className="max-w-4xl mx-auto">
            <button 
              onClick={onBack}
              className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
            >
              <Home className="w-5 h-5" />
              <span className="font-medium">Pratik Merkezi</span>
            </button>
            
            <div className="text-center text-white">
              <Mic className="w-12 h-12 mx-auto mb-2 text-yellow-300" />
              <h1 className="text-2xl font-bold">Kendini Kaydet</h1>
              <p className="text-purple-100 text-sm mt-1">Sesli veya görüntülü pratik yap</p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 mt-6">
          {/* Mode Selection */}
          <div className="flex justify-center gap-4 mb-6">
            <button
              onClick={() => setUseVideo(false)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                !useVideo ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-700'
              }`}
            >
              <Mic className="w-4 h-4" />
              Ses Kaydı
            </button>
            <button
              onClick={() => setUseVideo(true)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                useVideo ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-700'
              }`}
            >
              <Video className="w-4 h-4" />
              Video Kaydı
            </button>
          </div>

          {/* Saved Recordings */}
          {recordings.length > 0 && (
            <div className="mb-6">
              <h2 className="text-lg font-bold text-gray-800 mb-3">Kayıtlarım ({recordings.length})</h2>
              <div className="space-y-2">
                {recordings.slice(0, 3).map((rec) => (
                  <Card key={rec.id} className="cursor-pointer hover:shadow-md" onClick={() => {
                    setCurrentRecording(rec);
                    setMode('playback');
                  }}>
                    <CardContent className="p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {rec.type === 'video' ? <Video className="w-5 h-5 text-purple-600" /> : <Mic className="w-5 h-5 text-purple-600" />}
                        <div>
                          <p className="font-medium text-sm text-gray-800 line-clamp-1">{rec.question}</p>
                          <p className="text-xs text-gray-500">{rec.timestamp} • {formatTime(rec.duration)}</p>
                        </div>
                      </div>
                      <button 
                        onClick={(e) => { e.stopPropagation(); deleteRecording(rec.id); }}
                        className="p-2 text-red-500 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Questions */}
          <h2 className="text-lg font-bold text-gray-800 mb-3">Soru Seç</h2>
          <div className="space-y-3">
            {practiceQuestions.map((q) => (
              <Card 
                key={q.id}
                className="cursor-pointer hover:shadow-md transition-all"
                onClick={() => { setSelectedQuestion(q); setMode('record'); }}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{q.q}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${getDifficultyColor(q.difficulty)}`}>
                          {q.difficulty}
                        </span>
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {q.time} saniye
                        </span>
                      </div>
                    </div>
                    <Mic className="w-5 h-5 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Recording screen
  if (mode === 'record') {
    const timeRemaining = selectedQuestion.time - recordingTime;
    const progressPercent = (recordingTime / selectedQuestion.time) * 100;

    return (
      <div className="min-h-screen bg-gray-900 flex flex-col">
        <div className="p-4">
          <button 
            onClick={() => { stopRecording(); setMode('select'); }}
            className="flex items-center gap-2 text-white/80 hover:text-white"
          >
            <Home className="w-5 h-5" />
            <span className="font-medium">İptal</span>
          </button>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-4">
          {/* Video Preview */}
          {useVideo && (
            <div className="w-full max-w-md mb-6">
              <video 
                ref={videoRef} 
                className="w-full rounded-2xl bg-gray-800 transform scale-x-[-1]"
                muted
                playsInline
              />
            </div>
          )}

          {/* Question */}
          <Card className="w-full max-w-md mb-6">
            <CardContent className="p-4 text-center">
              <p className="text-lg font-medium text-gray-800">{selectedQuestion.q}</p>
            </CardContent>
          </Card>

          {/* Timer */}
          <div className="text-center mb-6">
            <div className={`text-6xl font-bold ${timeRemaining <= 10 ? 'text-red-500' : 'text-white'}`}>
              {formatTime(timeRemaining)}
            </div>
            <p className="text-gray-400 mt-2">kalan süre</p>
          </div>

          {/* Progress */}
          <div className="w-full max-w-md mb-8">
            <div className="bg-gray-700 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all ${timeRemaining <= 10 ? 'bg-red-500' : 'bg-purple-500'}`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Record Button */}
          <button
            onClick={isRecording ? stopRecording : startRecording}
            className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${
              isRecording 
                ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                : 'bg-purple-600 hover:bg-purple-700'
            }`}
          >
            {isRecording ? (
              <div className="w-8 h-8 bg-white rounded" />
            ) : (
              <Mic className="w-8 h-8 text-white" />
            )}
          </button>
          <p className="text-gray-400 mt-4">
            {isRecording ? 'Durdurmak için tıkla' : 'Başlatmak için tıkla'}
          </p>
        </div>
      </div>
    );
  }

  // Playback screen
  if (mode === 'playback' && currentRecording) {
    return (
      <div className="min-h-screen bg-gray-50 pb-8">
        <div className="bg-gradient-to-br from-purple-600 to-pink-600 px-4 pt-6 pb-8 rounded-b-[2rem]">
          <div className="max-w-4xl mx-auto">
            <button 
              onClick={() => setMode('select')}
              className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
            >
              <Home className="w-5 h-5" />
              <span className="font-medium">Pratik Merkezi</span>
            </button>
            
            <div className="text-center text-white">
              <Play className="w-12 h-12 mx-auto mb-2 text-yellow-300" />
              <h1 className="text-2xl font-bold">Kaydını Dinle</h1>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 mt-6">
          <Card className="mb-4">
            <CardContent className="p-4">
              <p className="font-medium text-gray-800 mb-2">{currentRecording.question}</p>
              <p className="text-sm text-gray-500">{currentRecording.timestamp} • {formatTime(currentRecording.duration)}</p>
            </CardContent>
          </Card>

          {/* Player */}
          <Card className="mb-6">
            <CardContent className="p-6">
              {currentRecording.type === 'video' ? (
                <video 
                  src={currentRecording.url} 
                  controls 
                  className="w-full rounded-lg transform scale-x-[-1]"
                />
              ) : (
                <audio 
                  ref={audioRef}
                  src={currentRecording.url} 
                  controls 
                  className="w-full"
                />
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-center gap-4">
            <Button 
              variant="outline"
              onClick={() => deleteRecording(currentRecording.id)}
              className="text-red-600 border-red-300"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Sil
            </Button>
            <Button onClick={() => { setSelectedQuestion(practiceQuestions.find(q => q.id === currentRecording.questionId) || practiceQuestions[0]); setMode('record'); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Tekrar Kaydet
            </Button>
          </div>

          {/* Tips */}

          {/* Saved Recordings from Previous Sessions */}
          {savedRecordings.length > 0 && (
            <Card className="mt-6 bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-800">📁 Önceki Kayıtlarım ({savedRecordings.length})</h3>
                  <Button variant="outline" size="sm" onClick={() => setShowSavedRecordings(!showSavedRecordings)}>
                    {showSavedRecordings ? "Gizle" : "Goster"}
                  </Button>
                </div>
                {showSavedRecordings && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {savedRecordings.map((rec, idx) => {
                      const getCategory = () => {
                        if (rec.category === "interview") return { label: "Mülakat", color: "bg-purple-100 text-purple-700" };
                        if (rec.level === "VOICE_PRACTICE") return { label: "Ses Pratiği", color: "bg-blue-100 text-blue-700" };
                        if (rec.level && rec.level.match(/^[0-9]+$/)) return { label: `Level ${rec.level}`, color: "bg-green-100 text-green-700" };
                        return { label: "Genel", color: "bg-gray-100 text-gray-700" };
                      };
                      const cat = getCategory();
                      return (
                      <div key={rec.id || idx} className="bg-white rounded-lg p-3 flex items-center gap-3 shadow-sm border border-gray-100">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cat.color}`}>{cat.label}</span>
                          </div>
                          <p className="text-sm font-medium text-gray-800 truncate">{rec.question_text || rec.exercise_title || "Kayit"}</p>
                          <p className="text-xs text-gray-500">{new Date(rec.created_at).toLocaleDateString("tr-TR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                        </div>
                        {(rec.audio_data || rec.audio_url) && (
                          <Button variant="ghost" size="sm" className="text-purple-600 hover:text-purple-700" onClick={() => { const audio = new Audio(rec.audio_data || rec.audio_url); audio.play(); }}>
                            <Play className="w-5 h-5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => deleteRecording(rec.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    );})}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card className="mt-6 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-gray-800 mb-2">💡 Değerlendirme İpuçları</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Sesin net ve anlaşılır mı?</li>
                <li>• Hızın uygun mu? (Çok hızlı/yavaş değil)</li>
                <li>• "Eee", "şey" gibi dolgu sesleri var mı?</li>
                <li>• Cevabın yapılandırılmış mı? (STAR tekniği)</li>
                <li>• Göz teması ve beden dili uygun mu? (Video için)</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return null;
}
